import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { useToastStore, type Toast as ToastModel } from '@/stores/toastStore';
import './ToastHost.scss';

// ToastHost is the SINGLETON renderer for the toast store. Mount it once at
// the app root (App.tsx) and any code can call `useToast().success(...)` /
// `.error(...)` / etc. from anywhere — the store is global, this component
// subscribes to it and renders the current queue.
//
// Why portal: toasts must escape the modal stacking context and the layout's
// overflow:hidden containers; otherwise a toast triggered from inside a
// modal renders behind the modal backdrop. Portaling into document.body
// puts the toast at the top of the DOM where its fixed-position CSS works
// regardless of which ancestor created a new stacking context.

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
} as const;

interface ToastRowProps {
  toast: ToastModel;
  onDismiss: (id: string) => void;
}

const ToastRow = ({ toast, onDismiss }: ToastRowProps) => {
  const Icon = ICONS[toast.kind];

  // Auto-dismiss is handled by the store via setTimeout. We ALSO support
  // manual dismiss (the X button) which calls the same store action so
  // the dedupe doesn't matter — set difference removes both ways.
  return (
    <div
      className={`toast toast--${toast.kind}`}
      role={toast.kind === 'error' ? 'alert' : 'status'}
      aria-live={toast.kind === 'error' ? 'assertive' : 'polite'}
    >
      <Icon size={18} className="toast__icon" aria-hidden="true" />
      <span className="toast__message">{toast.message}</span>
      <button
        type="button"
        className="toast__dismiss"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export const ToastHost = () => {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  // SSR-safety guard — `document` doesn't exist during a server-render
  // pass. We don't actually SSR today but keeping this conditional is
  // cheap insurance against a future Vite SSR setup blowing up on import.
  useEffect(() => {
    // no-op effect just to make this a "live" component for HMR. The
    // store subscription via `useToastStore` is what actually re-renders.
  }, []);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div className="toast-host" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastRow key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>,
    document.body,
  );
};
